/**
 * React Query hooks for email integrations.
 */
import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  integrationsService,
  ConnectEmailRequest,
  EmailIntegration,
  DiscoverProviderResponse,
  TestConnectionResponse,
  CheckNowResponse,
  UpdateScheduleRequest,
  UpdateSettingsRequest,
  FolderListResponse,
} from '../services/integrationsService';

/**
 * Query key factory for integrations.
 */
export const integrationKeys = {
  all: ['integrations'] as const,
  lists: () => [...integrationKeys.all, 'list'] as const,
  list: () => [...integrationKeys.lists()] as const,
  details: () => [...integrationKeys.all, 'detail'] as const,
  detail: (id: string) => [...integrationKeys.details(), id] as const,
};

/**
 * Hook to list all email integrations.
 * Auto-refreshes every 30 seconds to update status.
 */
export function useIntegrations() {
  // #1220: the first fetch on mount is the (interactive) page-load triggered by
  // the user navigating here — let the api interceptor tag it by gesture
  // recency. Every later fetch is the 30s background poll (or a
  // mutation-invalidation refetch, whose gesture was already tagged on the
  // mutation POST) — mark those background so a poll firing inside the 5s
  // post-gesture window never bumps "Last Active"/WAU.
  const polled = useRef(false);
  return useQuery({
    queryKey: integrationKeys.list(),
    queryFn: () => {
      const isBackgroundFetch = polled.current;
      polled.current = true;
      return integrationsService.listIntegrations(isBackgroundFetch ? { background: true } : undefined);
    },
    // #3431: check-now is 202 + background, and its outcome lands on `check_status`. 30s
    // is fine for ordinary status drift but a long time to watch a spinner for a check
    // that usually finishes in ~3s, so poll faster for exactly as long as one is running.
    refetchInterval: (query) =>
      query.state.data?.integrations?.some((i) => i.check_status?.status === 'running') ? 5000 : 30000,
  });
}

/**
 * Hook to get a single integration by ID.
 */
export function useIntegration(integrationId: string) {
  return useQuery({
    queryKey: integrationKeys.detail(integrationId),
    queryFn: () => integrationsService.getIntegration(integrationId),
    enabled: !!integrationId,
  });
}

/**
 * Hook to discover email provider from address.
 */
export function useDiscoverProvider() {
  return useMutation<DiscoverProviderResponse, Error, string>({
    mutationFn: (email: string) => integrationsService.discoverProvider(email),
  });
}

/**
 * Hook to test email credentials.
 *
 * #1852: imapHost/imapPort carry a manually-entered IMAP host for a
 * custom-domain provider (provider === 'unknown').
 */
export function useTestConnection() {
  return useMutation<
    TestConnectionResponse,
    Error,
    { email: string; password: string; imapHost?: string; imapPort?: number }
  >({
    mutationFn: ({ email, password, imapHost, imapPort }) =>
      integrationsService.testConnection(email, password, imapHost, imapPort),
  });
}

/**
 * Hook to connect email integration.
 * Invalidates integrations list on success.
 */
export function useConnectEmail() {
  const queryClient = useQueryClient();
  return useMutation<EmailIntegration, Error, ConnectEmailRequest>({
    mutationFn: (config: ConnectEmailRequest) => integrationsService.connectEmail(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

/**
 * Hook to trigger immediate email check.
 * Invalidates integrations list on success.
 */
export function useCheckNow() {
  const queryClient = useQueryClient();
  return useMutation<CheckNowResponse, Error, string>({
    mutationFn: (integrationId: string) => integrationsService.checkNow(integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

/**
 * Hook to update sync schedule settings.
 * Invalidates integrations list on success.
 */
export function useUpdateSchedule() {
  const queryClient = useQueryClient();
  return useMutation<EmailIntegration, Error, { integrationId: string; schedule: UpdateScheduleRequest }>({
    mutationFn: ({ integrationId, schedule }) =>
      integrationsService.updateSchedule(integrationId, schedule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

/**
 * Hook to update non-schedule email-integration settings (#1570).
 * Invalidates integrations list on success.
 */
export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation<EmailIntegration, Error, { integrationId: string; settings: UpdateSettingsRequest }>({
    mutationFn: ({ integrationId, settings }) =>
      integrationsService.updateSettings(integrationId, settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

/**
 * #3035: hook to list an already-connected mailbox's folder tree.
 *
 * A mutation rather than a query on purpose: each call costs a connect + LOGIN + LIST
 * against the customer's mail server, so it must fire exactly when the farmer opens the
 * editor — never on render, and never on a refetch/window-focus schedule.
 */
export function useListFolders() {
  return useMutation<FolderListResponse, Error, string>({
    mutationFn: (integrationId: string) => integrationsService.listFolders(integrationId),
  });
}

/**
 * Hook to disconnect email integration.
 * Invalidates integrations list on success.
 */
export function useDisconnectIntegration() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (integrationId: string) =>
      integrationsService.disconnectIntegration(integrationId).then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}
